import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FolderTree, ChevronRight, ChevronDown, X, Search,
  Building2, Briefcase, Package, Layers, FileText, Target, Check
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Button } from './ui/button';
import { Input } from './ui/input';

// ==========================================
// LEVEL ICONS
// ==========================================
const LEVEL_ICONS = {
  'Portfolio': Building2,
  'Program': Briefcase,
  'Project': Package,
  'Work Package': Layers,
  'Activity': FileText,
  'Task': Target,
  'Theme': FolderTree,
  'Epic': Layers,
  'Feature': Package,
  'Story': FileText,
};

const getIconForLevel = (levelName) => {
  return LEVEL_ICONS[levelName] || FileText;
};

// ==========================================
// TREE NODE FOR SELECTOR
// ==========================================

const SelectorTreeNode = ({
  node,
  depth,
  config,
  expandedIds,
  onToggle,
  selectedNodeId,
  onSelect,
  searchQuery,
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedNodeId === node.id;
  
  const levelConfig = config?.levels?.find(l => l.level === node.level) || {};
  const Icon = getIconForLevel(levelConfig.name);

  // Highlight matching text
  const highlightMatch = (text, query) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) => 
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-200 px-0.5 rounded">{part}</mark>
      ) : part
    );
  };

  return (
    <div>
      <div 
        style={{ paddingLeft: `${depth * 16 + 4}px` }}
        className={`flex items-center gap-1.5 py-1.5 px-2 rounded cursor-pointer transition-colors
          ${isSelected ? 'bg-purple-100 text-purple-700' : 'hover:bg-gray-50'}`}
        onClick={() => onSelect(node)}
        data-testid={`wbs-selector-node-${node.id}`}
      >
        {/* Expand/collapse */}
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
            className="p-0.5 hover:bg-gray-200 rounded"
          >
            {isExpanded ? (
              <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-gray-500" />
            )}
          </button>
        ) : (
          <span className="w-4" />
        )}

        {/* Icon with level color */}
        <Icon 
          className="w-3.5 h-3.5 flex-shrink-0"
          style={{ color: levelConfig.color || '#6366F1' }}
        />

        {/* Code */}
        <span className="text-xs font-mono text-gray-400">{node.code}</span>

        {/* Name */}
        <span className="text-sm truncate flex-1">
          {highlightMatch(node.name, searchQuery)}
        </span>

        {/* Selected indicator */}
        {isSelected && (
          <Check className="w-4 h-4 text-purple-600" />
        )}
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div>
          {node.children.map(child => (
            <SelectorTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              config={config}
              expandedIds={expandedIds}
              onToggle={onToggle}
              selectedNodeId={selectedNodeId}
              onSelect={onSelect}
              searchQuery={searchQuery}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ==========================================
// MAIN WBS NODE SELECTOR COMPONENT
// ==========================================

const WBSNodeSelector = ({
  value,
  onChange,
  placeholder = 'Select WBS Node',
  disabled = false,
  allowClear = true,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [tree, setTree] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedNode, setSelectedNode] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Load WBS tree
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, treeRes] = await Promise.all([
        api.getWBSConfig(),
        api.getWBSCoreTree(),
      ]);
      
      setConfig(configRes.data);
      setTree(treeRes.data?.tree || []);
      
      // Auto-expand all for searching
      const allIds = new Set();
      const collectIds = (nodes) => {
        for (const node of nodes) {
          allIds.add(node.id);
          if (node.children) collectIds(node.children);
        }
      };
      collectIds(treeRes.data?.tree || []);
      setExpandedIds(allIds);
    } catch (err) {
      console.error('Failed to load WBS data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load selected node details if value is provided
  useEffect(() => {
    if (value && !selectedNode) {
      api.getWBSCoreNode(value, false, false)
        .then(res => setSelectedNode(res.data))
        .catch(() => {});
    }
  }, [value, selectedNode]);

  // Load tree when opened
  useEffect(() => {
    if (open && tree.length === 0) {
      loadData();
    }
  }, [open, loadData, tree.length]);

  // Filter tree based on search
  const filteredTree = useMemo(() => {
    if (!searchQuery) return tree;

    const query = searchQuery.toLowerCase();
    
    const filterNodes = (nodes) => {
      const result = [];
      for (const node of nodes) {
        const matches = node.name.toLowerCase().includes(query) ||
                        node.code.toLowerCase().includes(query);
        
        const filteredChildren = node.children ? filterNodes(node.children) : [];
        
        if (matches || filteredChildren.length > 0) {
          result.push({
            ...node,
            children: filteredChildren,
          });
        }
      }
      return result;
    };

    return filterNodes(tree);
  }, [tree, searchQuery]);

  // Toggle expand
  const handleToggle = useCallback((id) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  // Select node
  const handleSelect = (node) => {
    setSelectedNode(node);
    onChange?.(node.id, node);
    setOpen(false);
    setSearchQuery('');
  };

  // Clear selection
  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedNode(null);
    onChange?.(null, null);
  };

  // Get level config for selected node
  const selectedLevelConfig = useMemo(() => {
    if (!selectedNode || !config) return null;
    return config.levels?.find(l => l.level === selectedNode.level);
  }, [selectedNode, config]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={`w-full justify-between font-normal ${className}`}
          data-testid="wbs-node-selector"
        >
          {selectedNode ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {selectedLevelConfig && (
                <span 
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: selectedLevelConfig.color }}
                />
              )}
              <span className="text-xs font-mono text-gray-400 flex-shrink-0">
                {selectedNode.code}
              </span>
              <span className="truncate">{selectedNode.name}</span>
            </div>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
          
          <div className="flex items-center gap-1">
            {allowClear && selectedNode && (
              <X 
                className="w-4 h-4 text-gray-400 hover:text-gray-600"
                onClick={handleClear}
              />
            )}
            <FolderTree className="w-4 h-4 text-gray-400" />
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-80 p-0" align="start">
        {/* Search */}
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search WBS nodes..."
              className="pl-8 h-8"
              autoFocus
              data-testid="wbs-selector-search"
            />
          </div>
        </div>

        {/* Tree */}
        <div className="max-h-64 overflow-y-auto p-1">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredTree.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              {searchQuery ? 'No matching nodes found' : 'No WBS structure defined'}
            </div>
          ) : (
            filteredTree.map(node => (
              <SelectorTreeNode
                key={node.id}
                node={node}
                depth={0}
                config={config}
                expandedIds={expandedIds}
                onToggle={handleToggle}
                selectedNodeId={value}
                onSelect={handleSelect}
                searchQuery={searchQuery}
              />
            ))
          )}
        </div>

        {/* Footer */}
        {selectedNode && (
          <div className="p-2 border-t bg-gray-50 text-xs text-gray-500">
            Selected: {selectedNode.code} - {selectedNode.name}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default WBSNodeSelector;
